import { Button } from "@mui/material";
import { useRef, useState } from "react";
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { useTranslation } from 'react-i18next';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// @breezystack/lamejs is an ES-module compatible fork — no MPEGMode global issue
import { Mp3Encoder } from '@breezystack/lamejs';

const getSupportedMimeType = (): string => {
    const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4'];
    return types.find(t => MediaRecorder.isTypeSupported(t)) || '';
};

const floatTo16Bit = (input: Float32Array): Int16Array => {
    const output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
        const s = Math.max(-1, Math.min(1, input[i]));
        output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return output;
};

const encodeToMp3 = (audioBuffer: AudioBuffer): File => {
    const channels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const encoder = new Mp3Encoder(channels === 1 ? 1 : 2, sampleRate, 128);

    const left = floatTo16Bit(audioBuffer.getChannelData(0));
    const right = channels > 1 ? floatTo16Bit(audioBuffer.getChannelData(1)) : left;

    const blockSize = 1152;
    const parts: Uint8Array[] = [];

    for (let i = 0; i < left.length; i += blockSize) {
        const l = left.subarray(i, i + blockSize);
        const r = right.subarray(i, i + blockSize);
        const buf = channels === 1 ? encoder.encodeBuffer(l) : encoder.encodeBuffer(l, r);
        if (buf.length > 0) parts.push(buf);
    }
    const flushed = encoder.flush();
    if (flushed.length > 0) parts.push(flushed);

    const blob = new Blob(parts as unknown as BlobPart[], { type: 'audio/mp3' });
    console.log('[AudioRecorder] MP3 size:', blob.size, 'bytes');
    return new File([blob], 'voice.mp3', { type: 'audio/mp3' });
};

export const AudioRecorder = ({ onDone = null as any, onStart = null as any }) => {
    const [permission, setPermission] = useState(false);
    const [recordingStatus, setRecordingStatus] = useState<string>("inactive");
    const [duration, setDuration] = useState<number>(0);
    const [audio, setAudio] = useState<any>(null);

    // useRef — never stale, readable from any callback without closure issues
    const mediaRecorder = useRef<any>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const mimeTypeRef = useRef<string>('');
    const timerRef = useRef<any>(null);

    const { t } = useTranslation();

    const startRecording = async () => {
        if (!("MediaRecorder" in window)) {
            alert(t("The MediaRecorder API is not supported in your browser."));
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            setPermission(true);
            setRecordingStatus("recording");
            setDuration(0);

            const mimeType = getSupportedMimeType();
            mimeTypeRef.current = mimeType;
            console.log('[AudioRecorder] mimeType:', mimeType || '(browser default)');

            audioChunksRef.current = [];
            const options = mimeType ? { mimeType } : {};
            const recorder = new MediaRecorder(stream, options);
            mediaRecorder.current = recorder;

            recorder.ondataavailable = (e: BlobEvent) => {
                if (!e.data || e.data.size === 0) return;
                audioChunksRef.current.push(e.data);
                console.log('[AudioRecorder] chunk', e.data.size, 'bytes — total chunks:', audioChunksRef.current.length);
            };

            recorder.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                const chunks = audioChunksRef.current;
                console.log('[AudioRecorder] stopped — chunks:', chunks.length);

                if (chunks.length === 0) {
                    console.error('[AudioRecorder] No audio data captured');
                    return;
                }

                const rawBlob = new Blob(chunks, { type: mimeTypeRef.current || 'audio/webm' });
                console.log('[AudioRecorder] raw blob size:', rawBlob.size, 'type:', rawBlob.type);

                try {
                    const arrayBuffer = await rawBlob.arrayBuffer();
                    const audioCtx = new AudioContext();
                    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
                    await audioCtx.close();

                    const mp3File = encodeToMp3(audioBuffer);
                    const audioUrl = URL.createObjectURL(mp3File);
                    setAudio(audioUrl);
                    onDone && onDone(mp3File);
                } catch (err) {
                    console.error('[AudioRecorder] Encoding failed:', err);
                    alert('Failed to encode audio. Please try again.');
                }

                audioChunksRef.current = [];
            };

            // timeslice=250 → ondataavailable fires every 250ms, not just on stop
            recorder.start(250);
            onStart && onStart();

            timerRef.current = window.setInterval(() => {
                setDuration(prev => prev + 1);
            }, 1000);
        } catch (err: any) {
            alert(err.message);
        }
    };

    const stopRecording = () => {
        setRecordingStatus("inactive");
        clearInterval(timerRef.current);
        mediaRecorder.current?.stop();
    };

    return (
        <div>
            <div className="audio-controls">
                {recordingStatus !== "recording" && (
                    <div onClick={startRecording} className="interview-buttons-long link-cursor purple-button">
                        <Button variant="outlined" color="primary">
                            <FiberManualRecordIcon /> {t('Start Recording')}
                        </Button>
                    </div>
                )}

                {permission && recordingStatus === "recording" && (
                    <div onClick={stopRecording} className="interview-buttons-long link-cursor purple-button">
                        <Button variant="outlined" color="primary">
                            <span className="blink flex-center">
                                <StopCircleIcon style={{ color: 'red' }} />
                                {t('Stop')} {new Date(duration * 1000).toISOString().slice(14, 19)}
                            </span>
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
